import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

// Connects to data-controller="admin-line-chart"
export default class extends Controller {
  static targets = ["chart", "periodDropdown"]
  static values = {
    series: Array,
    colors: Array,
    height: { type: Number, default: 310 },
    curve: { type: String, default: "straight" },
    showGrid: { type: Boolean, default: true },
    tooltipDateFormat: { type: String, default: "dd MMM yyyy" }
  }

  connect() {
    this.initChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  initChart() {
    const options = this.buildOptions()
    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }

  buildOptions() {
    const options = {
      series: this.seriesValue,
      legend: {
        show: false,
        position: "top",
        horizontalAlign: "left"
      },
      colors: this.colorsValue,
      chart: {
        fontFamily: "Satoshi, sans-serif",
        height: this.heightValue,
        id: "area-datetime",
        type: "area",
        toolbar: {
          show: false
        }
      },
      stroke: {
        curve: this.curveValue,
        width: 1
      },
      dataLabels: {
        enabled: false
      },
      markers: {
        size: 0
      },
      labels: {
        show: false,
        position: "top"
      },
      xaxis: {
        type: "datetime",
        tickAmount: 10,
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        },
        labels: {
          style: {
            colors: "#64748B",
            fontSize: "12px",
            fontFamily: "Satoshi, sans-serif"
          }
        }
      },
      yaxis: {
        labels: {
          style: {
            colors: "#64748B",
            fontSize: "12px",
            fontFamily: "Satoshi, sans-serif"
          },
          formatter: (value) => {
            return value.toFixed(2)
          }
        }
      },
      tooltip: {
        x: {
          format: this.tooltipDateFormatValue
        }
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.1,
          stops: [0, 90, 100]
        }
      },
      grid: {
        borderColor: "#3C4858",
        strokeDashArray: 5,
        xaxis: {
          lines: {
            show: this.showGridValue
          }
        },
        yaxis: {
          lines: {
            show: this.showGridValue
          }
        }
      },
      responsive: [
        {
          breakpoint: 1024,
          options: {
            chart: {
              height: 300
            }
          }
        },
        {
          breakpoint: 1366,
          options: {
            chart: {
              height: 320
            }
          }
        }
      ]
    }

    return options
  }

  selectPeriod(event) {
    const period = event.target.value

    // Dispatch custom event for period change
    this.dispatch("periodChange", { detail: { period } })
  }

  // Public method to update chart data
  updateSeries(newSeries) {
    if (this.chart) {
      this.chart.updateSeries(newSeries)
    }
  }

  // Public method to update chart options
  updateOptions(newOptions) {
    if (this.chart) {
      this.chart.updateOptions(newOptions)
    }
  }

  // Public method to update both series and options
  updateData(newSeries, newOptions = {}) {
    if (this.chart) {
      this.chart.updateOptions({
        series: newSeries,
        ...newOptions
      })
    }
  }

  // Public method to toggle series visibility
  toggleSeries(seriesName) {
    if (this.chart) {
      this.chart.toggleSeries(seriesName)
    }
  }

  // Public method to zoom to a specific date range
  zoomX(start, end) {
    if (this.chart) {
      this.chart.zoomX(start, end)
    }
  }

  // Public method to reset zoom
  resetZoom() {
    if (this.chart) {
      this.chart.resetSeries()
    }
  }
}
